import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Toaster } from 'sonner'
import { AuthProvider } from './lib/auth-context'
import { ProtectedRoute } from './components/layouts/ProtectedRoute'
import { PERMISSIONS } from './lib/permissions'
import { PublicRoute } from './components/layouts/PublicRoute'
import { Hotjar } from './components/features/Hotjar'
import { CannyWidget } from './components/features/CannyWidget'
import { isUntrackedPath } from './lib/untrackedRoutes'

// Pages
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import LoginSentPage from './pages/LoginSentPage'
import VerifyPage from './pages/VerifyPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import HomePage from './pages/HomePage'
import ProjectsNewPage from './pages/ProjectsNewPage'
import ProjectsIdPage from './pages/ProjectsIdPage'
import QuoteV2PreviewPage from './pages/QuoteV2PreviewPage'
import QuoteV2DraftEditorPage from './pages/QuoteV2DraftEditorPage'
import InterventionNewPage from './pages/InterventionNewPage'
import NewInvoicePage from './pages/NewInvoicePage'
import InvoicePreviewPage from './pages/InvoicePreviewPage'
import StandaloneInvoicePage from './pages/StandaloneInvoicePage'
import DirectInvoicePage from './pages/DirectInvoicePage'
import CreditNotePage from './pages/CreditNotePage'
import AdminPage from './pages/AdminPage'
import BusinessDashboardPage from './pages/BusinessDashboardPage'
import ClientDetailPage from './pages/ClientDetailPage'
import JobSiteEditPage from './pages/JobSiteEditPage'
import ProjectConfigEditPage from './pages/ProjectConfigEditPage'
import ClientEditPage from './pages/ClientEditPage'
import SettingsPage from './pages/SettingsPage'
import CatalogTranslationsPage from './pages/CatalogTranslationsPage'
import AlertSettingsPage from './pages/AlertSettingsPage'
import InvoicesPage from './pages/InvoicesPage'
import SupplierOrdersPage from './pages/SupplierOrdersPage'
import SuppliersPage from './pages/SuppliersPage'
import MailboxesPage from './pages/MailboxesPage'
import MailboxCallbackPage from './pages/MailboxCallbackPage'
import MicrosoftAdminConsentPage from './pages/MicrosoftAdminConsentPage'
import { MODULES } from './hooks/useModules'
import ActivityPage from './pages/ActivityPage'
import { DirectSaleOrderEditorPage } from './pages/DirectSaleOrderEditorPage'
import ProjectSaleSupplyPage from './pages/ProjectSaleSupplyPage'
import PlanningPage from './components/features/planning/PlanningPage'
import { OrdersRegistryPage } from './components/features/planning/registry/OrdersRegistryPage'

function App() {
  const { pathname } = useLocation()
  // SOLY-SUPPLIER-002 MS-05: no trackers where a customer's Microsoft administrator lands.
  const tracked = !isUntrackedPath(pathname)
  return (
    <AuthProvider>
      {/* Hotjar Analytics - loads automatically if VITE_HOTJAR_ID is set */}
      {tracked && <Hotjar />}
      {/* Canny Feedback - loads automatically if VITE_CANNY_APP_ID is set */}
      {tracked && <CannyWidget />}
      
      <Routes>
        {/* Public routes - redirect to dashboard if already logged in */}
        <Route
          path="/"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          }
        />
        <Route path="/login/sent" element={<LoginSentPage />} />
        {/* SOLY-SUPPLIER-002 MS-05 — a Microsoft administrator lands here after authorizing Soly. Public,
            outside PublicRoute (which redirects a signed-in person), untracked. */}
        <Route path="/microsoft/autorisation" element={<MicrosoftAdminConsentPage />} />
        <Route path="/verify" element={<VerifyPage />} />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPasswordPage />
            </PublicRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PublicRoute>
              <ResetPasswordPage />
            </PublicRoute>
          }
        />

        {/* Protected routes */}
        <Route
          path="/home"
          element={
            <ProtectedRoute>
              <HomePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/new"
          element={
            <ProtectedRoute>
              <ProjectsNewPage />
            </ProtectedRoute>
          }
        />
        {/* SOLY-REGIE-001 — the second door: a small file, one screen. ~600 a year against ~50
            for the wizard next door. */}
        <Route
          path="/interventions/new"
          element={
            <ProtectedRoute>
              <InterventionNewPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:id"
          element={
            <ProtectedRoute>
              <ProjectsIdPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/clients/:customerId"
          element={
            <ProtectedRoute>
              <ClientDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/job-sites/:jobSiteId/edit"
          element={
            <ProtectedRoute>
              <JobSiteEditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:projectId/job-site/new"
          element={
            <ProtectedRoute>
              <JobSiteEditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:id/configuration"
          element={
            <ProtectedRoute>
              <ProjectConfigEditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:id/commande-directe"
          element={
            <ProtectedRoute>
              <DirectSaleOrderEditorPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:id/vente"
          element={
            <ProtectedRoute module={MODULES.QUOTE_IMPORT}>
              <ProjectSaleSupplyPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/clients/:customerId/edit"
          element={
            <ProtectedRoute>
              <ClientEditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/projets/:id/devis-v2/:quoteId/versions/:versionId/preview"
          element={
            <ProtectedRoute>
              <QuoteV2PreviewPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/projets/:id/devis-v2/:quoteId/versions/:versionId/edit"
          element={
            <ProtectedRoute>
              <QuoteV2DraftEditorPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/projets/:id/invoices/new"
          element={
            <ProtectedRoute>
              <NewInvoicePage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/projets/:id/invoices/preview"
          element={
            <ProtectedRoute>
              <InvoicePreviewPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/projets/:id/invoices/standalone"
          element={
            <ProtectedRoute>
              <StandaloneInvoicePage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/projets/:id/invoices/direct"
          element={
            <ProtectedRoute>
              <DirectInvoicePage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/projets/:id/invoices/credit-note"
          element={
            <ProtectedRoute>
              <CreditNotePage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/business"
          element={
            <ProtectedRoute>
              <BusinessDashboardPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/alertes"
          element={
            <ProtectedRoute feature="planning" permission={PERMISSIONS.SETTINGS_MANAGE}>
              <AlertSettingsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/catalog/translations"
          element={
            <ProtectedRoute>
              <CatalogTranslationsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/invoices"
          element={
            <ProtectedRoute>
              <InvoicesPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/supplier-orders"
          element={
            <ProtectedRoute feature="planning" permission={PERMISSIONS.ORDERS_READ}>
              <SupplierOrdersPage />
            </ProtectedRoute>
          }
        />

        {/* SOLY-SUPPLIER-002 MS-02 — Outlook mailboxes, behind the purchase.mailbox module. The callback is
            where Microsoft sends the browser back after the sign-in. */}
        <Route
          path="/supplier-orders/mailboxes"
          element={
            <ProtectedRoute feature="planning" permission={PERMISSIONS.ORDERS_READ} module={MODULES.PURCHASE_MAILBOX}>
              <MailboxesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/supplier-orders/mailboxes/callback"
          element={
            <ProtectedRoute feature="planning" permission={PERMISSIONS.ORDERS_MANAGE} module={MODULES.PURCHASE_MAILBOX}>
              <MailboxCallbackPage />
            </ProtectedRoute>
          }
        />

        {/* SOLY-SUPPLIER-002 — the second tab of the orders space. */}
        <Route
          path="/supplier-orders/suppliers"
          element={
            <ProtectedRoute feature="planning" permission={PERMISSIONS.ORDERS_READ}>
              <SuppliersPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/activity"
          element={
            <ProtectedRoute>
              <ActivityPage />
            </ProtectedRoute>
          }
        />

        {/* The project detail is a drawer OVER the cockpit, driven by the URL: /planning shows the
            board, /planning/projects/:id shows the board with that project's drawer open. Nesting keeps
            PlanningPage mounted across the two (the agenda never reloads when opening/closing a project). */}
        <Route
          path="/planning"
          element={
            <ProtectedRoute feature="planning" permission={PERMISSIONS.PLANNING_READ}>
              <PlanningPage />
            </ProtectedRoute>
          }
        >
          <Route path="projects/:id" element={null} />
        </Route>

        <Route
          path="/planning/projects/:id/commandes"
          element={
            <ProtectedRoute feature="planning" permission={PERMISSIONS.ORDERS_READ}>
              <OrdersRegistryPage />
            </ProtectedRoute>
          }
        />

        {/* SOLY-TEAM-001: the skills page merged into the Équipe page */}
        <Route path="/admin/employees" element={<Navigate to="/admin" replace />} />
        {/* Legacy URL from before the SOLY-INV-004 rename; /tasks is deliberately
            NOT redirected (decommissioned — the catch-all handles it). */}
        <Route path="/factures" element={<Navigate to="/invoices?view=all" replace />} />

        {/* Catch all - redirect to login */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>

      {/* Global toast notifications */}
      <Toaster position="top-right" richColors />
    </AuthProvider>
  )
}

export default App
